import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Skeleton from "@material-ui/lab/Skeleton";
import CloseIcon from "@material-ui/icons/Close";
import A_Drawer from "../../../Atoms/Navigation/Drawers/A_Drawer";
import A_List from "../../../Atoms/DataDisplay/Lists/A_List";
import A_IconButton from "../../../Atoms/Inputs/Buttons/A_IconButton";
import M_DataStateRenderer from "../DataStateRenderer/M_DataStateRenderer";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as manageMenusAction from "../../../../../store/actions/manageMenusAction";
import PropTypes from "prop-types";

const DRAWER_WIDTH = 300;

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.60, 2),
    background: "linear-gradient(135deg, var(--sidebar-background) 0%, var(--header-background) 100%)",
    color: "var(--sidebar-fontcolor)",
    minHeight: 55,
    boxSizing: "border-box",
    borderBottom: "1px solid var(--total_row_boldbackground)",
    transition: "background 0.4s ease, color 0.4s ease",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  brandText: {
    fontWeight: 700,
    fontSize: "1rem",
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "var(--sidebar-fontcolor)",
  },
  brandIcon: {
    color: "var(--sidebar-fontcolor)",
    flexShrink: 0,
  },
  closeBtn: {
    color: "var(--sidebar-fontcolor)",
    opacity: 0.8,
    transition: "all 0.2s ease",
    "&:hover": {
      opacity: 1,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  skeletonWrap: {
    padding: theme.spacing(2),
    width: DRAWER_WIDTH,
    boxSizing: "border-box",
    backgroundColor: "var(--color-background-paper)",
  },
  skeletonRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  errorWrap: {
    padding: theme.spacing(2),
    width: DRAWER_WIDTH,
    boxSizing: "border-box",
  },
  listScope: {
    backgroundColor: "var(--color-background-paper)",
    transition: "background-color 0.4s ease, color 0.4s ease",
    "& .MuiListItem-root": {
      color: "var(--color-text-primary)",
      borderRadius: "calc(4px * var(--app-radius-scale, 1))",
      margin: "2px 8px",
      width: "auto",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "var(--total_row_background)",
        color: "var(--color-primary-text)",
        "& .MuiListItemIcon-root": {
          color: "var(--color-primary-text)",
        },
        "& .MuiListItemText-primary": {
          color: "var(--color-primary-text)",
        },
      },
      "&.Mui-selected": {
        backgroundColor: "var(--total_row_boldbackground)",
        color: "var(--color-primary-text)",
        "& .MuiListItemIcon-root": {
          color: "var(--color-primary-text)",
        },
      },
    },
    "& .MuiListItemIcon-root": {
      color: "var(--color-text-secondary)",
      minWidth: 36,
      transition: "color 0.2s ease",
    },
    "& .MuiListItemText-primary": {
      color: "inherit",
      fontWeight: 600,
      fontSize: "0.875rem",
      transition: "color 0.2s ease",
    },
    "& .MuiSvgIcon-root": {
      color: "inherit",
      fontSize: "1.2rem",
    },
    "& .MuiCollapse-container .MuiListItem-root": {
      paddingLeft: theme.spacing(2),
    },
  },
}));

const resetCollapseFlags = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    ...item,
    isopen: false,
    items: resetCollapseFlags(item.items),
  }));
};

const MenuSkeleton = ({ classes }) => (
  <Box className={classes.skeletonWrap} aria-label="Loading menu">
    {Array.from({ length: 7 }).map((_, idx) => (
      <Box key={idx} className={classes.skeletonRow}>
        <Skeleton variant="circle" width={28} height={28} />
        <Skeleton variant="text" width={`${60 + ((idx * 7) % 30)}%`} height={20} />
      </Box>
    ))}
  </Box>
);

MenuSkeleton.propTypes = {
  classes: PropTypes.object.isRequired,
};

const M_MainDrawer = (props) => {
  const { openDrawer, manageMenus, iconClicked, user, getMenus, handleDrawer } =
    props;
  const classes = useStyles();
  const [menusLoading, setMenusLoading] = useState(false);
  const [menus, setMenus] = useState(
    manageMenus && manageMenus.data && manageMenus.data.length > 0
      ? manageMenus.data
      : []
  );

  useEffect(() => {
    const alreadyLoaded =
      manageMenus && manageMenus.data && manageMenus.data.length > 0;
    if (!alreadyLoaded) {
      setMenusLoading(true);
      getMenus(user);
    }
  }, []);

  useEffect(() => {
    if (menusLoading && manageMenus && manageMenus.getMenusSucceeded) {
      setMenus(manageMenus.data);
      setMenusLoading(false);
    }
    if (menusLoading && manageMenus && manageMenus.getMenusFailed) {
      setMenusLoading(false);
    }
  }, [
    manageMenus && manageMenus.getMenusSucceeded,
    manageMenus && manageMenus.getMenusFailed,
  ]);

  useEffect(() => {
    if (menus && menus.length > 0) {
      setMenus((prev) => resetCollapseFlags(prev));
    }
  }, [iconClicked]);

  const isLoading = useMemo(
    () =>
      menusLoading ||
      (manageMenus && manageMenus.isLoading && (!menus || menus.length === 0)),
    [menusLoading, manageMenus, menus]
  );

  const error =
    manageMenus && manageMenus.getMenusFailed
      ? manageMenus.msg || "Failed to load menu"
      : null;

  const handleClose = (e) => {
    if (handleDrawer) handleDrawer(e);
  };

  const drawerContent = (
    <Box role="navigation" aria-label="Main menu">
      <Box className={classes.header}>
        <Box className={classes.brand}>
          <Typography variant="subtitle1" className={classes.brandText}>
            Menu
          </Typography>
        </Box>
        <A_IconButton
          size="small"
          aria-label="Close menu"
          className={classes.closeBtn}
          icon={<CloseIcon fontSize="small" />}
          onClick={handleClose}
        />
      </Box>
      <Divider />
      <Box className={classes.listScope}>
        <M_DataStateRenderer
          loading={isLoading}
          error={error}
          data={menus}
          isEmpty={!isLoading && !error && (!menus || menus.length === 0)}
          emptyMessage="NO MENU ITEMS AVAILABLE!"
          skeleton={<MenuSkeleton classes={classes} />}
        >
          <A_List
            listItems={menus}
            handleDrawer={(e) => handleDrawer && handleDrawer(e)}
          />
        </M_DataStateRenderer>
      </Box>
    </Box>
  );

  return (
    <div>
      <A_Drawer
        open={openDrawer}
        onClose={handleClose}
        anchor="left"
        width={DRAWER_WIDTH}
        content={drawerContent}
      />
    </div>
  );
};

function mapStateToProps(state) {
  return {
    manageMenus: state.manageMenus,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(manageMenusAction, dispatch);
}

M_MainDrawer.propTypes = {
  iconClicked: PropTypes.any,
  openDrawer: PropTypes.bool,
  manageMenus: PropTypes.shape({
    data: PropTypes.array,
    isLoading: PropTypes.bool,
    getMenusSucceeded: PropTypes.bool,
    getMenusFailed: PropTypes.bool,
    msg: PropTypes.string,
  }),
  user: PropTypes.any,
  getMenus: PropTypes.func,
  handleDrawer: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(M_MainDrawer);
